// src/capabilities/CaseStudiesTab.js
// Pestaña "Casos de estudio" del módulo Competencias: catálogo independiente
// (colección caseStudies). Eliminar = inactivar (soft delete).
// Ver PLAN_CASOS_ESTUDIO.md
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import CaseStudyModal from './CaseStudyModal';
import { useTranslation } from 'react-i18next';

const formatDate = (dateValue) => {
  if (!dateValue) return 'N/A';
  try {
    let date;
    if (dateValue._seconds) date = new Date(dateValue._seconds * 1000);
    else if (typeof dateValue.toDate === 'function') date = dateValue.toDate();
    else if (dateValue.seconds) date = new Date(dateValue.seconds * 1000);
    else date = new Date(dateValue);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) {
    return 'N/A';
  }
};

const CaseStudiesTab = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selected, setSelected] = useState(null);
  const [openDeactivateDialog, setOpenDeactivateDialog] = useState(false);
  const [toDeactivate, setToDeactivate] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { authToken, showAlert, user, language, organization } = useStore.getState();
  const { t } = useTranslation();
  const headers = { Authorization: `Bearer ${authToken}` };

  const canEdit = (cs) => {
    const userRole = user?.rol;
    if (userRole === 'super-admin') return true;
    if (['admin', 'admin-grafton', 'user'].includes(userRole)) return cs.organizationId === organization;
    return false;
  };

  const loadCaseStudies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/case-studies', {
        params: { language, organizationId: organization, includeInactive: true },
        headers,
      });
      if (response.data.response && response.data.response.status_response === 'OK') {
        setCaseStudies(response.data.response.data || []);
        setError(null);
      } else {
        setError(t('caseStudies.loadError'));
        showAlert(t('caseStudies.loadError'), 'warning');
      }
    } catch (err) {
      console.error('Error loading case studies:', err);
      setError(t('generalApiError'));
      showAlert(t('generalApiError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCaseStudies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return caseStudies
      .filter((cs) => showInactive || cs.isActive !== false)
      .filter((cs) => !term || (cs.title || '').toLowerCase().includes(term))
      .sort((a, b) => (a.title || '').localeCompare(b.title || '', 'es', { sensitivity: 'base' }));
  }, [caseStudies, searchTerm, showInactive]);

  useEffect(() => { setPage(0); }, [searchTerm, showInactive]);

  const handleOpenModal = (mode, cs = null) => {
    if (mode === 'edit' && cs && !canEdit(cs)) {
      showAlert(t('caseStudies.noPermissionToEdit'), 'warning');
      return;
    }
    setModalMode(mode);
    setSelected(cs);
    setOpenModal(true);
  };

  const handleDeactivate = (cs) => {
    if (!canEdit(cs)) {
      showAlert(t('caseStudies.noPermissionToEdit'), 'warning');
      return;
    }
    setToDeactivate(cs);
    setOpenDeactivateDialog(true);
  };

  const confirmDeactivate = async () => {
    try {
      setLoading(true);
      const id = toDeactivate.id || toDeactivate.uid;
      const response = await api.delete(`/case-studies/${id}`, {
        headers,
        data: { lastUpdatedBy: user?.email || user?.uid || 'unknown' },
      });
      if (response.data.response && response.data.response.status_response === 'OK') {
        await loadCaseStudies();
        showAlert(t('caseStudies.deactivated'), 'success');
      } else {
        showAlert(t('caseStudies.deactivateError'), 'error');
      }
    } catch (err) {
      console.error('Error deactivating case study:', err);
      showAlert(t('caseStudies.deactivateError'), 'error');
    } finally {
      setLoading(false);
      setOpenDeactivateDialog(false);
      setToDeactivate(null);
    }
  };

  const handleActivate = async (cs) => {
    if (!canEdit(cs)) {
      showAlert(t('caseStudies.noPermissionToEdit'), 'warning');
      return;
    }
    try {
      setLoading(true);
      const id = cs.id || cs.uid;
      const response = await api.put(`/case-studies/${id}/activate`,
        { lastUpdatedBy: user?.email || user?.uid || 'unknown' }, { headers });
      if (response.data.response && response.data.response.status_response === 'OK') {
        await loadCaseStudies();
        showAlert(t('caseStudies.activated'), 'success');
      } else {
        showAlert(t('caseStudies.saveError'), 'error');
      }
    } catch (err) {
      console.error('Error activating case study:', err);
      showAlert(t('caseStudies.saveError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading && caseStudies.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600 }}>
          {t('caseStudies.intro')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
            label={<Typography variant="body2">{t('caseStudies.showInactive')}</Typography>}
          />
          <TextField
            variant="outlined"
            placeholder={t('caseStudies.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon color="action" fontSize="small" /></InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ width: 300, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal('create')} sx={{ borderRadius: 2 }}>
            {t('caseStudies.newCaseStudy')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {filtered.length === 0 && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {searchTerm ? `${t('caseStudies.noneFound')} "${searchTerm}"` : t('caseStudies.none')}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto', maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('title')}</TableCell>
              <TableCell>{t('caseStudies.readingTime')}</TableCell>
              <TableCell>{t('lastModified')}</TableCell>
              <TableCell align="center">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((cs) => (
              <TableRow key={cs.id || cs.uid} sx={cs.isActive === false ? { opacity: 0.6 } : undefined}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body1" fontWeight="medium">{cs.title}</Typography>
                    {!cs.organizationId && (
                      <Chip label={language === 'es' ? 'Por defecto' : 'Default'} color="primary" variant="outlined" size="small" />
                    )}
                    {cs.isActive === false && (
                      <Chip label={t('caseStudies.inactive')} color="default" size="small" />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: 600,
                  }}>
                    {cs.content}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={`${cs.readingTime || 5} min`} variant="outlined" size="small" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{formatDate(cs.modified)}</Typography>
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleOpenModal('view', cs)} title={t('show')}>
                    <ViewIcon />
                  </IconButton>
                  {canEdit(cs) && cs.isActive !== false && (
                    <>
                      <IconButton size="small" onClick={() => handleOpenModal('edit', cs)} title={t('edit')}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeactivate(cs)} title={t('caseStudies.deactivate')} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                  {canEdit(cs) && cs.isActive === false && (
                    <IconButton size="small" onClick={() => handleActivate(cs)} title={t('caseStudies.activate')} color="primary">
                      <RestoreIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        sx={{ mr: 6 }}
      />

      <CaseStudyModal
        open={openModal}
        onClose={() => { setOpenModal(false); setSelected(null); }}
        mode={modalMode}
        selectedCaseStudy={selected}
        onSave={loadCaseStudies}
        user={user}
        authToken={authToken}
        showAlert={showAlert}
        language={language}
      />

      <Dialog open={openDeactivateDialog} onClose={() => setOpenDeactivateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('caseStudies.deactivateTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('caseStudies.deactivateConfirm')} <strong>"{toDeactivate?.title}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('caseStudies.deactivateHint')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeactivateDialog(false)} color="inherit">{t('cancel')}</Button>
          <Button onClick={confirmDeactivate} color="error" variant="contained" disabled={loading}>
            {t('caseStudies.deactivate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaseStudiesTab;
